import React, { useMemo } from "react";
import { Assets } from "../../../../constants/assets";
import { useAppTranslation } from "../../../../i18n/useAppTranslation";
import ThousandsSeparator from "../../../../utils/thousands-separator";
import { summarizeShopDescription } from "../../../../utils/arena-shop-card";
import "./shop-card.css";

// The currency of a shop item's price (the quest's required action).
const PRICE_ICONS: Record<number, string> = {
  33: Assets.icons.bloodStone,
  34: "/assets/images/icons/cleg.png",
};

// The fields of an Arena shop reward (GET /quest/arenashops) the card reads.
type ShopItem = {
  title: string;
  icon: string;
  description: string;
  requiredAction: number;
  requiredActionValue: number;
  rewards?: { level?: number }[];
};

type Props = {
  reward: ShopItem;
  onOpen: () => void;
};

/**
 * One Arena shop item in the Arena dialog: art, level, name, its stat and price.
 * The stat is read out of the admin's description (see summarizeShopDescription)
 * rather than rendered as HTML.
 */
const ShopCard = ({ reward, onOpen }: Props) => {
  const { t } = useAppTranslation("battle");
  const text = useMemo(
    () => summarizeShopDescription(reward.description),
    [reward.description]
  );
  const title = reward.title?.trim();
  const level = reward.rewards?.[0]?.level;
  const priceIcon = PRICE_ICONS[reward.requiredAction];

  return (
    <div
      role="button"
      tabIndex={0}
      title={title}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        onOpen();
      }}
      className="item-frame ashop-card button pointer"
    >
      {level ? <span className="ashop-level">{t("shop.level", { level })}</span> : null}
      <div className="ashop-art">
        <img src={reward.icon} alt={title} />
      </div>
      <div className="ashop-name">{title}</div>
      {text.kind === "stats" ? (
        <div className="ashop-stats">
          {text.stats.slice(0, 4).map((stat, i) => (
            <span key={i} className="ashop-chip" title={stat.label}>
              {stat.icon && <img src={stat.icon} alt={stat.label} />}
              {stat.value}
            </span>
          ))}
        </div>
      ) : (
        (text.main || text.icon) && (
          <>
            <div className={`ashop-stat${text.target ? "" : " ashop-stat--wrap"}`}>
              {text.icon && <img src={text.icon} alt="" />}
              <span>{text.main}</span>
            </div>
            {text.target && <div className="ashop-target">{text.target}</div>}
          </>
        )
      )}
      <div className="ashop-price">
        {priceIcon && <img src={priceIcon} alt="" />}
        {ThousandsSeparator(reward.requiredActionValue)}
      </div>
    </div>
  );
};

export default ShopCard;
